import { useAppContext } from '@faro/app-context';
import {
    CommandResponse,
    ExclusionSection,
    PlacementPayload,
    RenamePayload,
    RevisionedCommandResponse,
    StudyEntityType,
    StudySection,
} from '@faro/study-designer-model';
import { createSectionDuplicateMutation, SectionDuplicateHook } from '../createSectionDuplicateMutation';
import { createSectionInsertMutation, SectionInsertHook } from '../createSectionInsertMutation';
import {
    useExclusionSectionDelete,
    useExclusionSectionLink,
    useExclusionSectionMove,
    useExclusionSectionRename,
    useExclusionSectionUpdate,
} from './ExclusionSectionHooks';

export type ExclusionSectionInsertHook = SectionInsertHook<Partial<ExclusionSection>>;

/**
 * Mutation hook to insert an exclusion section.
 * @returns A function that inserts an exclusion section.
 */
export const useExclusionSectionInsertMutation: (studyId: string) => ExclusionSectionInsertHook =
    createSectionInsertMutation<Partial<ExclusionSection>>(
        StudyEntityType.ExclusionSection,
        'exclusion-sections',
        'Failed to create exclusion section'
    );

export type ExclusionSectionUpdateHook = (
    exclusionSection: ExclusionSection
) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to update an exclusion section.
 * @returns A function that updates an exclusion section.
 */
export function useExclusionSectionUpdateMutation(studyId: string): ExclusionSectionUpdateHook {
    const { showErrorHandler } = useAppContext();

    const updateExclusionSection = useExclusionSectionUpdate({ studyId });

    function update(exclusionSection: ExclusionSection) {
        return updateExclusionSection
            .mutateAsync(exclusionSection)
            .catch(showErrorHandler('Failed to update exclusion section'));
    }

    return update;
}

export type ExclusionSectionDeleteHook = (exclusionSection: {
    id: string;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to delete an exclusion section.
 * @returns A function that deletes an exclusion section.
 */
export function useExclusionSectionDeleteMutation(studyId: string): ExclusionSectionDeleteHook {
    const { showErrorHandler } = useAppContext();

    const deleteExclusionSection = useExclusionSectionDelete({ studyId });

    function deleteSection(exclusionSection: { id: string }) {
        return deleteExclusionSection
            .mutateAsync({
                id: exclusionSection.id,
                studyId,
            })
            .catch(showErrorHandler('Failed to delete exclusion section'));
    }

    return deleteSection;
}

export type ExclusionSectionDuplicateHook = SectionDuplicateHook;

/**
 * Mutation hook to duplicate an exclusion section.
 * @returns A function that duplicates an exclusion section.
 */
export const useExclusionSectionDuplicateMutation: (studyId: string) => ExclusionSectionDuplicateHook =
    createSectionDuplicateMutation(
        StudyEntityType.ExclusionSection,
        'exclusion-sections',
        'Failed to duplicate exclusion section'
    );

export type ExclusionSectionMoveHook = (exclusionSection: {
    id: string;
    placement: PlacementPayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to move an exclusion section.
 * @returns A function that moves an exclusion section.
 */
export function useExclusionSectionMoveMutation(studyId: string): ExclusionSectionMoveHook {
    const { showErrorHandler } = useAppContext();

    const moveExclusionSection = useExclusionSectionMove({ studyId });

    function move(exclusionSection: { id: string; placement: PlacementPayload }) {
        return moveExclusionSection
            .mutateAsync({
                ...exclusionSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to move exclusion section'));
    }

    return move;
}

export type ExclusionSectionLinkHook = (exclusionSection: {
    id: string;
    linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to link an exclusion section.
 * @returns A function that links an exclusion section.
 */
export function useExclusionSectionLinkMutation(studyId: string): ExclusionSectionLinkHook {
    const { showErrorHandler } = useAppContext();

    const linkExclusionSection = useExclusionSectionLink({ studyId });

    function link(exclusionSection: {
        id: string;
        linkedSection: Partial<StudySection> & Pick<StudySection, 'id' | 'studyId' | 'entityType'>;
    }) {
        return linkExclusionSection
            .mutateAsync({
                ...exclusionSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to link exclusion section'));
    }

    return link;
}

export type ExclusionSectionRenameHook = (exclusionSection: {
    id: string;
    renamePayload: RenamePayload;
}) => Promise<RevisionedCommandResponse<CommandResponse>>;

/**
 * Mutation hook to rename an exclusion section.
 * @returns A function that renames an exclusion section.
 */
export function useExclusionSectionRenameMutation(studyId: string): ExclusionSectionRenameHook {
    const { showErrorHandler } = useAppContext();

    const renameExclusionSection = useExclusionSectionRename({ studyId });

    function rename(exclusionSection: { id: string; renamePayload: RenamePayload }) {
        return renameExclusionSection
            .mutateAsync({
                ...exclusionSection,
                studyId,
            })
            .catch(showErrorHandler('Failed to rename exclusion section'));
    }

    return rename;
}
